// ABOUTME: React Query hook for fetching unloading methods configuration from the backend API.
// ABOUTME: Calls GET /tuc-configs/unloading-methods, finds config for selected scope, transforms to form-ready shape.

import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'

import {api} from '../../../../App.store'
import {UnloadingMethodsApiResponse, UnloadingMethodsConfig} from '../types/unloadingMethods'

// A row returned for a scope means values are set at that scope (an override relative to the parent).
// The BE _meta carries audit fields only — inheritance is derived client-side (match vs. fallback).
const transformResponse = (response: UnloadingMethodsApiResponse): UnloadingMethodsConfig => ({
  id: response.id,
  values: response.values ?? [],
  parentValues: [],
  overrides: {
    values: true,
  },
  _meta: {
    scopeId: response.scopeId,
    isInherited: false,
    resolvedFrom: null,
    createdAt: response._meta.createdAt,
    updatedAt: response._meta.updatedAt,
    createdBy: response._meta.createdBy,
    updatedBy: response._meta.updatedBy,
  },
})

export const useUnloadingMethodsConfig = (
  countryId: string | undefined,
  businessLine: string | undefined,
  scopeId: string | undefined,
  rootScopeId?: string | undefined
): {data: UnloadingMethodsConfig | undefined; isLoading: boolean; error: Error | null} => {
  const {data: rawData, isLoading, error} = useQuery<UnloadingMethodsApiResponse[], Error>({
    queryKey: ['unloadingMethodsConfig', countryId, businessLine],
    queryFn: async () => {
      const res = await api.get<UnloadingMethodsApiResponse[]>('/tuc-configs/unloading-methods', {
        params: {countryId, businessLine: businessLine!.toUpperCase()},
      })
      return res.data ?? []
    },
    enabled: Boolean(countryId && businessLine && scopeId),
    staleTime: 30_000,
  })

  const data = useMemo(() => {
    if (!rawData || !scopeId) return undefined

    // Country-default (root) selection — used to flag per-code overrides against the parent.
    const rootRow = rootScopeId ? rawData.find((r) => r.scopeId === rootScopeId) : rawData[0]
    const parentValues = rootRow?.values ?? []

    const match = rawData.find((r) => r.scopeId === scopeId)
    if (match) return {...transformResponse(match), parentValues}

    // No row for the selected scope, but a parent/root config exists → inherit from it.
    if (rootRow) {
      const inherited = transformResponse(rootRow)
      return {
        ...inherited,
        id: undefined,
        _meta: {...inherited._meta, isInherited: true},
        overrides: {values: false},
        parentValues,
      }
    }

    // No rows at all (e.g. a brand-new country default) — render an empty, creatable config so
    // the user can select methods and POST the first row.
    return {
      id: undefined,
      values: [],
      parentValues: [],
      overrides: {values: false},
      _meta: {
        scopeId,
        isInherited: false,
        resolvedFrom: null,
        createdAt: '',
        updatedAt: '',
        createdBy: '',
        updatedBy: '',
      },
    }
  }, [rawData, scopeId, rootScopeId])

  return {data, isLoading, error}
}
